"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRight, Images } from "lucide-react";

import { GalleryStartButton } from "@/components/gallery/gallery-start-button";
import { trackGalleryCtaClicked } from "@/lib/posthog-client";
import { cn } from "@/lib/utils";

type GalleryPreviewItem = {
  id: string;
  imageUrl: string;
  wheelName: string;
};

export function HomeGalleryCta() {
  const [items, setItems] = useState<GalleryPreviewItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    fetch("/api/gallery?limit=5", { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load gallery previews");
        return response.json() as Promise<{ items?: GalleryPreviewItem[] }>;
      })
      .then((data) => setItems(data.items ?? []))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setItems([]);
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, []);

  return (
    <section
      id="community-results"
      className="relative overflow-hidden border-b border-white/5 bg-[#0A0C0F] px-6 py-24 md:py-32"
    >
      <div className="pointer-events-none absolute left-0 top-1/3 h-96 w-96 rounded-full bg-white/[0.025] blur-3xl" />
      <div className="relative mx-auto max-w-7xl">
        <div className="grid items-end gap-6 md:grid-cols-[1fr_auto]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/35">
              From the community
            </p>
            <h2 className="mt-4 max-w-2xl text-balance text-4xl font-bold tracking-tight text-white md:text-6xl">
            See real previews from the community
          </h2>
          </div>
          <p className="max-w-sm text-base leading-relaxed text-white/50 md:pb-2 md:text-right">
            A few of the free-preview results selected for our public gallery.
          </p>
        </div>

        <div className="mt-12 rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.055] to-white/[0.02] p-3 shadow-2xl shadow-black/20 md:p-5">
          <div className="mb-4 flex items-center justify-between gap-4 px-2 py-1">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.06]">
                <Images className="h-4 w-4 text-white/70" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-semibold text-white">
                  Other users&apos; results
                </p>
                <p className="text-xs text-white/35">
                  Recent wheel previews from the community
                </p>
              </div>
            </div>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/40">
              Updated regularly
            </span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="aspect-[4/3] animate-pulse rounded-2xl border border-white/8 bg-white/[0.04]"
                />
              ))}
            </div>
          ) : items.length > 0 ? (
            <ul
              className={cn(
                "grid list-none gap-3",
                items.length <= 3
                  ? "mx-auto max-w-5xl sm:grid-cols-3"
                  : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
              )}
            >
              {items.map((item, index) => (
                <li key={item.id}>
                  <div className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
                    <Image
                      src={item.imageUrl}
                      alt={item.wheelName}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      sizes="(max-width: 768px) 50vw, 20vw"
                    />
                    <span className="absolute left-3 top-3 rounded-full border border-white/15 bg-black/60 px-2.5 py-1 text-[10px] font-semibold text-white/70 backdrop-blur-sm">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-10">
                      <p className="truncate text-xs font-medium text-white/80">
                        {item.wheelName}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-2xl border border-white/8 bg-white/[0.03] px-6 py-12 text-center text-sm text-white/45">
              New gallery selections are being reviewed.
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-5 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-6 py-5 sm:flex-row">
          <p className="text-center text-sm text-white/45 sm:text-left">
            Get inspired, then create a result using your own car.
          </p>
          <div className="flex w-full flex-col items-center gap-4 sm:w-auto sm:flex-row">
          <GalleryStartButton
            trackLocation="home_after_showcase"
              className="w-full bg-white text-black hover:bg-white/90 sm:w-auto"
          >
            Try it on my car
          </GalleryStartButton>
          <Link
            href="/gallery"
            onClick={() =>
              trackGalleryCtaClicked({ location: "home_after_showcase" })
            }
              className="inline-flex items-center gap-1.5 text-sm font-medium text-white/55 transition-colors hover:text-white"
          >
            Explore the full gallery
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
